import React, { useState, useEffect, useRef } from "react";
import { RecursionCommands } from "../../../types/RecursionCommands";
import styles from "../Recursion.module.css";

type Token = {
  key: string;
  type: "paren" | "operator" | "number" | "call" | "result";
  content: string;
};

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

interface FactorialEngineProps {
  command: RecursionCommands | null;
  onComplete: () => void;
  inputValue: string;
  setActiveLine: React.Dispatch<React.SetStateAction<number | null>>;
  isAnimating: boolean;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setFinalResult: React.Dispatch<React.SetStateAction<number | null>>;
  finalResult: number | null;
}

const FactorialEngine: React.FC<FactorialEngineProps> = ({
  command,
  onComplete,
  inputValue,
  setActiveLine,
  isAnimating,
  setIsAnimating,
  setFinalResult,
  finalResult,
}) => {
  const [steps, setSteps] = useState<Token[][]>([]);
  const stopRef = useRef(false);

  const expandFactorial = async (
    n: number,
    currentSteps: Token[][]
  ): Promise<number> => {
    if (stopRef.current) throw new Error("stopped");

    if (n <= 1) {
      setActiveLine(2);
      await delay(400);
      if (stopRef.current) throw new Error("stopped");

      setActiveLine(3);
      await delay(400);
      if (stopRef.current) throw new Error("stopped");

      const last = [...currentSteps[currentSteps.length - 1]];
      last[last.length - 1] = {
        key: `r1_${Math.random()}`,
        type: "result",
        content: "1",
      };
      setSteps([...currentSteps, last]);
      await delay(400);
      if (stopRef.current) throw new Error("stopped");

      return 1;
    }

    setActiveLine(6);
    await delay(400);
    if (stopRef.current) throw new Error("stopped");

    const prev = [...currentSteps[currentSteps.length - 1]];
    prev.pop();
    prev.push(
      { key: `p${n}-1_${Math.random()}`, type: "paren", content: "(" },
      { key: `op${n}_${Math.random()}`, type: "operator", content: `* ${n} ` },
      { key: `c${n - 1}_${Math.random()}`, type: "call", content: `factorial(${n - 1})` },
      { key: `p${n}-2_${Math.random()}`, type: "paren", content: ")" }
    );

    const newSteps = [...currentSteps, prev];
    setSteps(newSteps);
    await delay(400);
    if (stopRef.current) throw new Error("stopped");

    const child = await expandFactorial(n - 1, newSteps);
    const resultHere = n * child;

    setActiveLine(6);
    await delay(400);
    if (stopRef.current) throw new Error("stopped");

    const last2 = [...newSteps[newSteps.length - 1]];
    const callIdx = [...last2]
      .reverse()
      .findIndex((t) => t.type === "call" && t.content.startsWith("factorial("));
    if (callIdx !== -1) {
      const idx = last2.length - 1 - callIdx;
      last2.splice(idx, 1, {
        key: `r${n}_${Math.random()}`,
        type: "result",
        content: String(child),
      });
    }

    setSteps([...newSteps, last2]);
    await delay(400);
    if (stopRef.current) throw new Error("stopped");

    return resultHere;
  };

  const reset = () => {
    stopRef.current = true; 
    setSteps([]);
    setFinalResult(null);
    setActiveLine(null);
    setIsAnimating(false);
  };

  const executeCommand = async (cmd: RecursionCommands) => {
    switch (cmd.action) {
      case "run":
        if (isAnimating) return;

        const n = parseInt(inputValue, 10);
        if (isNaN(n) || n < 0 || n > 10) {
          alert("Please enter a number between 0 and 10.");
          return;
        }

        stopRef.current = false;
        setFinalResult(null);
        setIsAnimating(true);

        const init: Token[][] = [
          [{ key: `c0_${Math.random()}`, type: "call", content: `factorial(${n})` }],
        ];
        setSteps(init);
        setActiveLine(0);
        await delay(400);
        if (stopRef.current) break;

        try {
          const res = await expandFactorial(n, init);
          setFinalResult(res);
        } catch {
        } finally {
          setIsAnimating(false);
          setActiveLine(null);
          onComplete();
        }
        break;

      case "stop":
        stopRef.current = true;
        setIsAnimating(false);
        setActiveLine(null);
        break;

      case "reset":
        reset();
        break;
    }
  };

  useEffect(() => {
    if (command) executeCommand(command);
  }, [command]);

  return (
    <div className={styles.substitutionViz}>
      {steps.map((step, index) => (
        <div key={index} className={styles.stepLine}>
          {step.map((token) => (
            <span key={token.key} className={styles[`token-${token.type}`]}>
              {token.content}
            </span>
          ))}
        </div>
      ))}    
       {finalResult !== null && (
        <div className={styles.resultLine}>→ Result: {finalResult}</div>
      )}
    </div>
  );
};

export default FactorialEngine;
